import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import useFilterParams from "../../hooks/useFilterParams";
import type { LeaveRequest } from "../../types";
import { formatLeaveStatus, formatLeaveTypeName, leaveStatusTone } from "../../utils/formatters";

const DATE_FMT = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

const LEAVE_FILTERS = { status: "pending" };

export default function AdminLeavePage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<LeaveRequest[]>([]);
  const [filters, setFilter] = useFilterParams(LEAVE_FILTERS);
  const [error, setError] = useState<string | null>(null);
  const [amendingId, setAmendingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const qs = filters.status ? `?status=${filters.status}` : "";
      setRows(await api.get<LeaveRequest[]>(`/api/leave/team-requests${qs}`));
    } catch (err) {
      setError((err as Error).message);
    }
  }, [filters.status]);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = async (id: number) => {
    await api.put(`/api/leave/requests/${id}/approve`, { notes: "" });
    await load();
  };

  const reject = async (id: number) => {
    const notes = prompt(t("leave.rejectReason")) || "";
    if (!notes.trim()) return;
    await api.put(`/api/leave/requests/${id}/reject`, { notes });
    await load();
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">{t("leave.title")}</h1>
        <p className="text-sm text-gray-500">
          {t("leave.allRequests", "All leave requests across the organisation.")}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-600">{t("leave.statusFilter", "Status")}:</span>
        {(["pending", "approved", "rejected", "cancelled", ""] as const).map((s) => (
          <button
            key={s || "all"}
            type="button"
            onClick={() => setFilter("status", s)}
            className={[
              "rounded-full px-3 py-1 text-xs font-semibold",
              filters.status === s
                ? "bg-pme-blue text-white"
                : "bg-gray-100 text-gray-700",
            ].join(" ")}
          >
            {s ? formatLeaveStatus(s) : t("common.all", "All")}
          </button>
        ))}
      </div>

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      {/* Mobile card layout */}
      <div className="space-y-2 md:hidden">
        {rows.length === 0 && (
          <p className="py-6 text-center text-sm text-gray-400">{t("leave.noRequests")}</p>
        )}
        {rows.map((r) => (
          <div
            key={r.id}
            className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {r.employee_name}
                </p>
                <p className="text-xs text-gray-500">{r.employee_number}</p>
              </div>
              <span
                className={[
                  "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                  leaveStatusTone(r.status),
                ].join(" ")}
              >
                {formatLeaveStatus(r.status)}
              </span>
            </div>
            <p className="mt-2 text-xs text-gray-600">
              <span
                aria-hidden
                className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
                style={{ backgroundColor: r.leave_type_colour ?? "#0872c1" }}
              />
              {formatLeaveTypeName(r.leave_type_code, r.leave_type_name)} · {r.total_days} {t("leave.days").toLowerCase()}
            </p>
            <p className="text-xs text-gray-500">
              {DATE_FMT.format(new Date(r.start_date))} →{" "}
              {DATE_FMT.format(new Date(r.end_date))}
            </p>
            {r.status === "pending" && (
              amendingId === r.id ? (
                <AmendForm
                  request={r}
                  onDone={() => { setAmendingId(null); void load(); }}
                  onCancel={() => setAmendingId(null)}
                />
              ) : (
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => void approve(r.id)}
                    className="min-h-[44px] flex-1 rounded-lg bg-green-600 text-sm font-semibold text-white"
                  >
                    {t("leave.approve")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAmendingId(r.id)}
                    className="min-h-[44px] flex-1 rounded-lg bg-orange-500 text-sm font-semibold text-white"
                  >
                    {t("leave.amend")}
                  </button>
                  <button
                    type="button"
                    onClick={() => void reject(r.id)}
                    className="min-h-[44px] flex-1 rounded-lg bg-red-600 text-sm font-semibold text-white"
                  >
                    {t("leave.reject")}
                  </button>
                </div>
              )
            )}
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-x-auto rounded-xl border border-gray-200 bg-white md:block">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">{t("leave.employee")}</th>
              <th className="px-3 py-2">{t("leave.leaveType")}</th>
              <th className="px-3 py-2">{t("leave.dates")}</th>
              <th className="px-3 py-2 text-right">{t("leave.days")}</th>
              <th className="px-3 py-2">{t("leave.statusLabel", "Status")}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-3 py-6 text-center text-xs text-gray-400"
                >
                  {t("leave.noRequests")}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2">
                  <p className="font-medium text-gray-900">{r.employee_name}</p>
                  <p className="text-xs text-gray-500">{r.employee_number}</p>
                </td>
                <td className="px-3 py-2">
                  <span
                    aria-hidden
                    className="mr-2 inline-block h-2 w-2 rounded-full align-middle"
                    style={{
                      backgroundColor: r.leave_type_colour ?? "#0872c1",
                    }}
                  />
                  {formatLeaveTypeName(r.leave_type_code, r.leave_type_name)}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {DATE_FMT.format(new Date(r.start_date))} →{" "}
                  {DATE_FMT.format(new Date(r.end_date))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {r.total_days}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={[
                      "rounded-full px-2 py-0.5 text-xs font-semibold",
                      leaveStatusTone(r.status),
                    ].join(" ")}
                  >
                    {formatLeaveStatus(r.status)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {r.status === "pending" && (
                    amendingId === r.id ? (
                      <AmendForm
                        request={r}
                        onDone={() => { setAmendingId(null); void load(); }}
                        onCancel={() => setAmendingId(null)}
                      />
                    ) : (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => void approve(r.id)}
                          className="rounded bg-green-600 px-2 py-1 text-xs font-semibold text-white"
                        >
                          {t("leave.approve")}
                        </button>
                        <button
                          type="button"
                          onClick={() => setAmendingId(r.id)}
                          className="rounded bg-orange-500 px-2 py-1 text-xs font-semibold text-white"
                        >
                          {t("leave.amend")}
                        </button>
                        <button
                          type="button"
                          onClick={() => void reject(r.id)}
                          className="rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white"
                        >
                          {t("leave.reject")}
                        </button>
                      </div>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AmendForm({
  request,
  onDone,
  onCancel,
}: {
  request: LeaveRequest;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(request.start_date);
  const [endDate, setEndDate] = useState(request.end_date);
  const [halfStart, setHalfStart] = useState(false);
  const [halfEnd, setHalfEnd] = useState(false);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!notes.trim()) {
      setError(t("leave.rejectReason"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.put(`/api/leave/requests/${request.id}/amend`, {
        start_date: startDate,
        end_date: endDate,
        is_half_day_start: halfStart,
        is_half_day_end: halfEnd,
        notes: notes.trim(),
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-3 space-y-3 rounded-lg border border-orange-200 bg-orange-50/50 p-3">
      <p className="text-xs font-semibold text-orange-800">
        {t("leave.originalRequest")}: {DATE_FMT.format(new Date(request.start_date))} →{" "}
        {DATE_FMT.format(new Date(request.end_date))} ({request.total_days} {t("leave.days").toLowerCase()})
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="block text-xs">
          <span className="font-medium text-gray-700">{t("leave.startDate")}</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="input mt-1"
          />
        </label>
        <label className="block text-xs">
          <span className="font-medium text-gray-700">{t("leave.endDate")}</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="input mt-1"
          />
        </label>
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={halfStart} onChange={(e) => setHalfStart(e.target.checked)} />
          {t("leave.halfDayStart")}
        </label>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={halfEnd} onChange={(e) => setHalfEnd(e.target.checked)} />
          {t("leave.halfDayEnd")}
        </label>
      </div>
      <label className="block text-xs">
        <span className="font-medium text-gray-700">{t("leave.amendmentNotes")} *</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          required
          className="input mt-1"
        />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="min-h-[44px] rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          {submitting ? t("common.loading") : t("leave.submitAmendment")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[44px] rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
        >
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}
